import React from "react";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface IncomeProtectionFormProps {
  formValues: Record<string, any>;
  onFieldChange: (fieldId: string, value: any) => void;
  className?: string;
  participants?: Array<{ id: string; vorname: string; nachname: string }>;
}

// Insurance categories and their items
const INSURANCE_SECTIONS = [
  {
    items: [
      { id: "unfall", label: "Unfall" },
    ],
  },
  {
    items: [
      { id: "berufsunfaehigkeit", label: "Berufsunfähigkeit" },
      { id: "grundfaehigkeit", label: "Grundfähigkeit" },
    ],
  },
  {
    items: [
      { id: "taggeld", label: "Taggeld" },
      { id: "buft", label: "BUFT" },
    ],
  },
  {
    items: [
      { id: "ableben", label: "Ableben" },
      { id: "begraebnis", label: "Begräbniskosten" },
    ],
  },
];

export function IncomeProtectionForm({
  formValues,
  onFieldChange,
  className,
  participants = [],
}: IncomeProtectionFormProps) {
  const displayParticipants = participants.length > 0 
    ? participants 
    : [
        { id: "gp1", vorname: "Gesprächspartner", nachname: "1" },
        { id: "gp2", vorname: "Gesprächspartner", nachname: "2" },
      ];

  const getParticipantLabel = (participant: typeof displayParticipants[0], index: number) => {
    const name = `${participant.vorname || ''} ${participant.nachname || ''}`.trim();
    return name || `GP${index + 1}`;
  };

  const renderInsuranceTable = (
    sectionIndex: number,
    items: Array<{ id: string; label: string }>
  ) => {
    const sectionKey = `income_protection_section_${sectionIndex}`;
    const sectionData: Record<string, Record<string, { checked: boolean; gesellschaft: string }>> = 
      formValues[sectionKey] || {};
    const noExisting = formValues[`${sectionKey}_none`] || false;
    const copyPhoto = formValues[`${sectionKey}_copy`] || false;

    const updateItem = (
      itemId: string,
      participantId: string,
      field: 'checked' | 'gesellschaft',
      value: boolean | string
    ) => {
      const itemData = sectionData[itemId] || {};
      const participantData = itemData[participantId] || { checked: false, gesellschaft: "" };
      const updated = {
        ...sectionData,
        [itemId]: {
          ...itemData,
          [participantId]: {
            ...participantData,
            [field]: value,
          },
        },
      };
      onFieldChange(sectionKey, updated);
    };

    return (
      <div key={sectionIndex} className="space-y-3">
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-primary text-primary-foreground">
                <th className="text-left p-3 font-semibold">Sparte</th>
                {displayParticipants.map((participant, pIdx) => (
                  <React.Fragment key={participant.id}>
                    <th className="text-center p-3 font-semibold whitespace-nowrap">
                      {getParticipantLabel(participant, pIdx).slice(0, 4)}
                    </th>
                    <th className="text-center p-3 font-semibold">Gesellschaft</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t">
                  <td className="p-3 font-medium text-sm">{item.label}</td>
                  {displayParticipants.map((participant) => (
                    <React.Fragment key={participant.id}>
                      <td className="p-2 text-center">
                        <Checkbox
                          checked={sectionData[item.id]?.[participant.id]?.checked || false}
                          onCheckedChange={(checked) =>
                            updateItem(item.id, participant.id, 'checked', !!checked)
                          }
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={sectionData[item.id]?.[participant.id]?.gesellschaft || ""}
                          onChange={(e) =>
                            updateItem(item.id, participant.id, 'gesellschaft', e.target.value)
                          }
                          className="h-8 text-sm"
                          placeholder=""
                        />
                      </td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex gap-6">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={noExisting}
              onCheckedChange={(checked) => onFieldChange(`${sectionKey}_none`, checked)}
            />
            <Label className="text-sm cursor-pointer">keine vorhanden</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={copyPhoto}
              onCheckedChange={(checked) => onFieldChange(`${sectionKey}_copy`, checked)}
            />
            <Label className="text-sm cursor-pointer">Kopie oder Foto</Label>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={cn("p-6 space-y-8", className)}>
      {/* Title */}
      <div className="text-left">
        <h2 className="text-2xl md:text-3xl font-bold">
          <span className="text-foreground">Einkommen </span>
          <span className="text-primary">sichern</span>
        </h2>
        <p className="text-lg italic text-muted-foreground mt-1">
          Sicherheit für Ihr regelmäßiges Einkommen.
        </p>
      </div>

      {/* Insurance Sections */}
      {INSURANCE_SECTIONS.map((section, index) => (
        <div key={index}>
          {renderInsuranceTable(index, section.items)}
        </div>
      ))}

      {/* Notes Section */}
      <div className="space-y-3">
        <div className="border-2 border-muted rounded-lg p-4">
          <Label className="text-sm font-semibold text-foreground">Notizen:</Label>
          <Textarea
            value={formValues.income_protection_notes || ""}
            onChange={(e) => onFieldChange("income_protection_notes", e.target.value)}
            placeholder="Ihre Notizen hier..."
            className="mt-2 min-h-[100px] resize-none"
          />
        </div>
      </div>
    </div>
  );
}
